import { useEffect, useRef } from "react";

type Point = {
  x: number;
  y: number;
  dx: number;
  dy: number;
};

const SPACING = 22;
const RADIUS = 92;
const DOT_SIZE = 1.15;
const BASE_ALPHA = 0.18;
const ACTIVE_ALPHA = 0.54;
const RETURN_EASE = 0.075;
const REPEL_FORCE = 16;

export function LoginParticlesBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const mediaReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mediaTouch = window.matchMedia("(hover: none), (pointer: coarse)");
    const mouse = { x: -9999, y: -9999, active: false };
    let points: Point[] = [];
    let raf = 0;
    let width = 0;
    let height = 0;
    let dpr = 1;

    const buildGrid = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cols = Math.ceil(width / SPACING) + 2;
      const rows = Math.ceil(height / SPACING) + 2;
      const offsetX = (width - (cols - 1) * SPACING) / 2;
      const offsetY = (height - (rows - 1) * SPACING) / 2;
      points = [];

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          points.push({
            x: offsetX + col * SPACING,
            y: offsetY + row * SPACING,
            dx: 0,
            dy: 0,
          });
        }
      }
    };

    const shouldAnimate = () => !mediaReduce.matches && !mediaTouch.matches;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const animated = shouldAnimate() && mouse.active;

      for (const point of points) {
        let targetX = 0;
        let targetY = 0;
        let intensity = 0;

        if (animated) {
          const mx = point.x + point.dx - mouse.x;
          const my = point.y + point.dy - mouse.y;
          const distance = Math.hypot(mx, my);
          if (distance > 0 && distance < RADIUS) {
            intensity = 1 - distance / RADIUS;
            const force = intensity * intensity * REPEL_FORCE;
            targetX = (mx / distance) * force;
            targetY = (my / distance) * force;
          }
        }

        point.dx += (targetX - point.dx) * RETURN_EASE;
        point.dy += (targetY - point.dy) * RETURN_EASE;

        ctx.beginPath();
        ctx.fillStyle = `rgba(255, 255, 255, ${BASE_ALPHA + intensity * (ACTIVE_ALPHA - BASE_ALPHA)})`;
        ctx.arc(point.x + point.dx, point.y + point.dy, DOT_SIZE + intensity * 0.35, 0, Math.PI * 2);
        ctx.fill();
      }

      if (shouldAnimate()) {
        raf = window.requestAnimationFrame(draw);
      }
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      mouse.x = event.clientX;
      mouse.y = event.clientY;
      mouse.active = true;
    };

    const handlePointerLeave = () => {
      mouse.active = false;
    };

    const redrawStatic = () => {
      window.cancelAnimationFrame(raf);
      buildGrid();
      draw();
    };

    redrawStatic();
    window.addEventListener("resize", redrawStatic);
    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    window.addEventListener("pointerleave", handlePointerLeave);
    mediaReduce.addEventListener("change", redrawStatic);
    mediaTouch.addEventListener("change", redrawStatic);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", redrawStatic);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerleave", handlePointerLeave);
      mediaReduce.removeEventListener("change", redrawStatic);
      mediaTouch.removeEventListener("change", redrawStatic);
    };
  }, []);

  return <canvas ref={canvasRef} className="login-particles-canvas" aria-hidden="true" />;
}
